import { useState } from 'react';
import { Check, Copy, Shield, ShieldAlert, ShieldCheck } from 'lucide-react';

interface HashDisplayProps {
  hash: string;
  label?: string;
  verified?: boolean | null;
  showFull?: boolean;
  copyable?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function HashDisplay({
  hash,
  label = 'SHA-256',
  verified = null,
  showFull = false,
  copyable = true,
  size = 'md',
  className = '',
}: HashDisplayProps) {
  const [copied, setCopied] = useState(false);

  const truncatedHash = showFull 
    ? hash 
    : `${hash.slice(0, 8)}...${hash.slice(-8)}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(hash);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  const VerificationIcon = verified === true 
    ? ShieldCheck 
    : verified === false 
    ? ShieldAlert 
    : Shield;

  const verificationColor = verified === true
    ? 'text-green-600'
    : verified === false
    ? 'text-red-600'
    : 'text-gray-400';

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {verified !== null && (
        <span title={verified ? 'Hash verified' : 'Hash verification failed'}>
          <VerificationIcon 
            className={`${iconSizes[size]} ${verificationColor}`}
          />
        </span>
      )}
      
      <div className="flex flex-col">
        {label && (
          <span className={`${sizeClasses[size]} text-gray-500 font-medium`}>
            {label}
          </span>
        )}
        <code 
          className={`${sizeClasses[size]} font-mono bg-gray-100 px-2 py-1 rounded text-gray-700 select-all`}
          title={hash}
        >
          {truncatedHash}
        </code>
      </div>
      
      {copyable && (
        <button
          onClick={handleCopy}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          title="Copy hash"
        >
          {copied ? (
            <Check className={`${iconSizes[size]} text-green-600`} />
          ) : (
            <Copy className={iconSizes[size]} />
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Compact version for table cells
 */
export function HashBadge({ 
  hash, 
  verified 
}: { 
  hash: string; 
  verified?: boolean | null;
}) {
  const truncated = `${hash.slice(0, 6)}...${hash.slice(-4)}`;
  
  const bgColor = verified === true
    ? 'bg-green-50 text-green-700'
    : verified === false
    ? 'bg-red-50 text-red-700'
    : 'bg-gray-50 text-gray-700';

  return (
    <code className={`text-xs font-mono px-2 py-0.5 rounded ${bgColor}`}>
      {truncated}
    </code>
  );
}

export default HashDisplay;
